/* global Sys, Core, Services, Resources */
Sys.ns("Core.Slots");

/**
 * The Response Parser controller for slots.
 *
 * @class Core.Slots.ResponseParserController
 * @extends Core.ResponseParserController
 */
Core.Slots.ResponseParserController = {
    constructor: function() {
        Core.Slots.ResponseParserController.superclass.constructor.apply(this, arguments);
    },

    /**
     * Extends the base function to calculate win type and provide some more structured data.
     *
     * @private
     * @param {string} responseText The server request response text.
     * @returns {Object} The response parsed into an object.
     */
    parseServerResponse: function(responseText) {
        var me = this,
            parsedResponse = Core.Slots.ResponseParserController.superclass.parseServerResponse.call(me, responseText),
            config = Resources.readData("config"),
            reelInfo;

        if (parsedResponse.gamestate) {
            parsedResponse.currentGameState = me.getCurrentGameState(parsedResponse);
        }

        if (parsedResponse.rs) {
            parsedResponse.rs = this.sortReelSets(parsedResponse.rs, config.expectedReelSetOrder);

            reelInfo = this.parseReelInfo(parsedResponse);
            parsedResponse.reelInfo = reelInfo;

            parsedResponse.nearWinList = this.parseNearWin(parsedResponse);

            if (parsedResponse.ws) {
                parsedResponse.wins = me.parseWinSituations(parsedResponse, reelInfo);
                parsedResponse.wins.winType = me.setWinType(parsedResponse.wins.coins, Services.moneyManager.getBetCoins());
                parsedResponse.wins.centsTotal = parsedResponse.totalwin.cents;
                parsedResponse.wins.coinsTotal = parsedResponse.totalwin.coins;
            }
            else if (Sys.isDefined(parsedResponse.totalwin)) {
                parsedResponse.wins = {
                    centsTotal: parsedResponse.totalwin.cents,
                    coinsTotal: parsedResponse.totalwin.coins
                };
            }
        }
        else if (Sys.isDefined(parsedResponse.totalwin)) {
            parsedResponse.wins = {
                centsTotal: parsedResponse.totalwin.cents,
                coinsTotal: parsedResponse.totalwin.coins
            };
        }

        return parsedResponse;
    },

    /**
     * Sorts the reel sets according to the provided structure.
     * If no order is provided the default sorting is made alphabetically.
     *
     * @private
     * @param {Object} reelSets The initially parsed reel sets.
     * @param {string[]} [order] The expected order (optional).
     * @returns {Object} The sorted object.
     */
    sortReelSets: function(reelSets, order) {
        return Object.keys(reelSets)
            .map(function(key) {
                return reelSets[key];
            })
            .sort(function(a, b) {
                // sorts based on order array
                if (order) {
                    if (order.indexOf(a.id) === -1 || order.indexOf(b.id) === -1) {
                        throw new Error("Missing order of item.");
                    }
                    return order.indexOf(a.id) > order.indexOf(b.id) ? 1 : -1;
                }
                // fallback to sort alphabetically
                return a.id > b.id ? 1 : -1;
            })
            .reduce(function(acc, value, index) {
                acc["i" + index] = value;
                return acc;
            }, {});
    },

    /**
     * Utility function that returns what we define as the current game state (which does not always equal ["gamestate.current"]).
     *
     * @param {Object} serverResponse The raw server data as an object (parsed by ServerManager).
     * @returns {string} The name of the game state which we consider the current game state.
     */
    getCurrentGameState: function(serverResponse) {
        /*
         * The server response parameters contains two important parameters: gamestate.current and gamestate.history
         * gamestate.current is the string name of the gamestate the server is in.
         * This is not always what we call the current gamestate as the server transitions between states before we do.
         * For instance, when we get a basic spin response on which we win freespins gamestate.current will be "freespin".
         * When we get that server response however we still have one win presentation left before the client will
         * enter freespins and we consider this win presentation to take place in basic mode.
         * gamestate.current is thus the game state in which the next action will be taken.
         */

        var me = this,
            previousServerGameState = me.model.readData("currentServerGameState");

        // We don't use the currentServerGameState for the current spin, but we save it for the next client spin state.
        if (serverResponse.gamestate.current) {
            me.model.storeData("currentServerGameState", serverResponse.gamestate.current);
        }
        else {
            me.model.storeData("currentServerGameState", serverResponse.gamestate);
        }

        // init
        if (!previousServerGameState) {
            previousServerGameState = me.model.readData("currentServerGameState");

            if (previousServerGameState === "basic") {
                me.fireEvent("request:playModeManager.addMode", "basic");
            }
        }

        return previousServerGameState;
    },

    /**
     * Extracts the near win list from the server response.
     *
     * @param {Object} parsedResponse The server response after basic parsing.
     * @returns {number[]|undefined} The near win array or undefined if no near win information was found.
     */
    parseNearWin: function(parsedResponse) {
        var nearWinInfo;

        if (Sys.isDefined(parsedResponse.rs.i0)) {
            nearWinInfo = parsedResponse.rs.i0.nearwin;
            if (Sys.isArray(nearWinInfo)) {
                return nearWinInfo;
            }
            else if (Sys.isNumber(nearWinInfo)) {
                return [nearWinInfo];
            }
        }
        return undefined;
    },

    /**
     * Sets win type based on coins won and players bet.
     *
     * @private
     * @param {number} coinsWon Win in coins.
     * @param {number} coinsBet Players bet in coins.
     * @returns {string} The win type.
     */
    setWinType: function(coinsWon, coinsBet) {
        var config = Resources.readData("config"),
            winTypeConfig = config.winTypes,
            winType = "";

        // Classify win
        if (coinsWon > 0) {
            // Small win
            if ((coinsWon >= coinsBet * winTypeConfig.smallWin.from) && (coinsWon < coinsBet * winTypeConfig.smallWin.to)) {
                winType = "smallWin";
            }
            // Medium win
            else if ((coinsWon >= coinsBet * winTypeConfig.mediumWin.from) && (coinsWon < coinsBet * winTypeConfig.mediumWin.to)) {
                winType = "mediumWin";
            }
            // Large win
            else if ((coinsWon >= coinsBet * winTypeConfig.largeWin.from) && (coinsWon < coinsBet * winTypeConfig.largeWin.to)) {
                winType = "largeWin";
            }
            // Big win
            else if (coinsWon >= coinsBet * winTypeConfig.bigWin.from) {
                winType = "bigWin";
            }
        }

        return winType;
    },

    /**
     * @param {Object} parsedServerResponse Server response as parsed by Core.ResponseParserController.parseServerResponse.
     * @returns {Object[]} Parsed reel info.
     */
    parseReelInfo: function(parsedServerResponse) {
        return Sys.utils.parseReelInfo(parsedServerResponse);
    },

    /**
     * @private
     * @param {Object} parsedServerResponse Server response as parsed by Core.ResponseParserController.parseServerResponse.
     * @param {Object[]} reelInfo The reelInfo as parsed by parseReelInfo.
     * @returns {{winSituations: Object[], coins: number, cents: number, freespins: number}} Parsed win situations.
     */
    parseWinSituations: function(parsedServerResponse, reelInfo) {
        var winSituationIndex = 0,
            winSituations = [],
            winInfo;

        if (parsedServerResponse.ws) {
            while (parsedServerResponse.ws["i" + winSituationIndex]) {
                winSituations.push(parsedServerResponse.ws["i" + winSituationIndex]);
                winSituationIndex++;
            }
        }

        winInfo = this.parseWinLayouts(winSituations, reelInfo);
        this.doGameSpecificWinSituationParsing(winInfo, winSituations);

        return winInfo;
    },

    /**
     * The format in which we receive the raw win situation data
     * @class rawWinSituation
     */

    /**
     * @property {Number|null} betline The betline this win situation represents
     */

    /**
     * @property {String} reelset The reel set used for win situation WS
     */

    /**
     * @property {Object} pos Object holding the positions of the symbols active in win situation WS on the form iX : [reel index, symbol index]
     */

    /**
     * @property {Object} types Object holding the wins active in win situation WS on the form iX : {wintype : {"coins"|"freespins"}, coins : Number, cents : Number, freespins : Number}
     */

    /**
     * @private
     * @param {rawWinSituation[]} winSituations The win situations as parsed by parseWinSituations.
     * @param {Object[]} reelInfo The reelInfo as parsed by parseReelInfo.
     * @returns {{winSituations: Object[], coins: number, cents: number, freespins: number}} Parsed win situation.
     */
    parseWinLayouts: function(winSituations, reelInfo) {
        var wi,
            pi,
            ti,
            winLayouts = [],
            symbolInfo,
            coinWin = 0,
            centWin = 0,
            freespinWin = 0;

        for (wi = 0; wi < winSituations.length; wi++) {
            winLayouts.push({
                // The betlines are 0-indexed on the server but 1-indexed in the game
                betline: Sys.isNumber(winSituations[wi].betline) ? winSituations[wi].betline + 1 : undefined,
                positions: [],
                reelset: winSituations[wi].reelset,
                wins: {}
            });

            if (Sys.isDefined(winSituations[wi].pos)) {
                for (pi = 0; Sys.isDefined(winSituations[wi].pos["i" + pi]); pi++) {
                    // Example:
                    // winSituations[0].pos.i0=[0,0]
                    // winSituations[0].pos.i2=[1,0]
                    // winSituations[0}.pos.i1=[2,0]
                    symbolInfo = {};
                    symbolInfo.reelIndex = winSituations[wi].pos["i" + pi][0];
                    symbolInfo.symbolIndex = winSituations[wi].pos["i" + pi][1];

                    symbolInfo = Sys.apply(symbolInfo, reelInfo[symbolInfo.reelIndex].symbols[symbolInfo.symbolIndex]);

                    // Push the position
                    winLayouts[wi].positions.push(symbolInfo);
                }

                winLayouts[wi].positions.sort(this.sortSymbols);
            }

            // For every win type of the win situation...
            for (ti = 0; Sys.isDefined(winSituations[wi].types["i" + ti]) && Sys.isDefined(winSituations[wi].types["i" + ti].wintype); ti++) {
                winLayouts[wi].wins.type = winSituations[wi].types["i" + ti].wintype;

                if (winSituations[wi].types["i" + ti].wintype === "coins") {
                    winLayouts[wi].wins.coins = winSituations[wi].types["i" + ti].coins;
                    winLayouts[wi].wins.cents = winSituations[wi].types["i" + ti].cents;
                    coinWin += winSituations[wi].types["i" + ti].coins;
                    centWin += winSituations[wi].types["i" + ti].cents;
                }
                else if (winSituations[wi].types["i" + ti].wintype === "freespins") {
                    winLayouts[wi].wins.freespins = winSituations[wi].types["i" + ti].freespins;
                    freespinWin += winSituations[wi].types["i" + ti].freespins;
                }
                else {
                    // No game uses this yet.
                }
            }
        }

        winLayouts.sort(this.sortWinSituations);

        return {
            winSituations: winLayouts,
            coins: coinWin,
            cents: centWin,
            freespins: freespinWin
        };
    },

    /**
     * Sorts symbols in ascending reel order.
     *
     * @private
     * @param {Object} sym1 A symbol object.
     * @param {Object} sym2 A symbol object.
     * @returns {number} A positive number indicates that sym2 should come after sym1.
     */
    sortSymbols: function(sym1, sym2) {
        return sym1.reelIndex - sym2.reelIndex;
    },

    /**
     * Sorts win situations descending with coins > freespins.
     *
     * @private
     * @param {Object} ws1 A win situation.
     * @param {Object} ws2 A win situation.
     * @returns {number} A positive number indicates that ws2 should come before ws1 (i.e. is bigger).
     */
    sortWinSituations: function(ws1, ws2) {
        if (ws1.wins.coins) {
            if (ws2.wins.coins) {
                return ws2.wins.coins - ws1.wins.coins;
            }
            return -1;
        }
        else if (ws2.wins.coins) {
            return 1;
        }

        return ws2.wins.freespins - ws1.wins.freespins;
    },

    /**
     * Stub method you can override if you need to parse any special information from the win situations.
     *
     * @private
     * @param {Object} winInfo The object where the win information will be stored in the server response object.
     * @param {Object[]} winSituations An array with all the win situation objects as parsed from the server response.
     * @returns {void}
     */
    doGameSpecificWinSituationParsing: function(winInfo, winSituations) {
        // stub
    }
};

Core.Slots.ResponseParserController = Sys.extend(Core.ResponseParserController, Core.Slots.ResponseParserController, "Core.Slots.ResponseParserController");
